import { useContext } from 'react';
import classNames from 'classnames';
import {
  MembershipPackage,
  MembershipPackageName,
  MembershipPackageRecurring,
} from 'common/interfaces/api';
import { DISCOUNT_TO_100_YEN_SILVER_EVENT } from 'common/utils';
import {
  addMembershipClickEvent,
  checkIsChoosingSubbingMembershipPackageWithNameOnly,
  featureContent,
  freeTrialRangeText,
  membershipPricePackagePeriod,
  packageLabel,
} from 'common/utils/membership';
import { ScreenName } from 'common/utils/pp_tracking';
import Icon from 'components/common/Icon';
import dayjs from 'lib/dayjs';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import { MembershipPaymentSectionContext } from '..';
import styles from './MembershipDescription.module.scss';

const MembershipDescription = (): JSX.Element => {
  const {
    membershipPackages,
    currentPackage,
    setCurrentPackage,
    isProcessing,
    screenName,
    choosingIzanaviPackage,
    izanaviPackage,
    currentPaymentBy,
  } = useContext(MembershipPaymentSectionContext);
  const currentUser = useStore((state) => state.currentUser);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  // const isMobile = useMediaQuery('(max-width: 875px)');
  const isOpenFromTradeOpeningLP = screenName === ScreenName.TakaTradeOpeningLP;
  const showLogin = useGlobalStore((state) => state.showLogin);
  const greenPackage = membershipPackages.find(
    (pack) =>
      pack.package_name === MembershipPackageName.Green &&
      pack.recurring === MembershipPackageRecurring.Month,
  );
  const silverPackage = membershipPackages.find(
    (pack) =>
      pack.package_name === MembershipPackageName.Silver &&
      pack.recurring === MembershipPackageRecurring.Month,
  );
  const goldPackage = membershipPackages.find(
    (pack) =>
      pack.package_name === MembershipPackageName.Gold &&
      pack.recurring === MembershipPackageRecurring.Month,
  );
  const platinumPackage = membershipPackages.find(
    (pack) =>
      pack.package_name === MembershipPackageName.Platinum &&
      pack.recurring === MembershipPackageRecurring.Month,
  );
  if (membershipPackages.length === 0 || !currentPackage) return <></>;
  const subStatusText = currentUser?.membership?.is_trialing
    ? '無料体験中'
    : '現在のプラン';
  const isReSubscription = currentUser?.is_subscribed_membership_before;
  const havingMembershipPackage = currentUser?.membership?.membership_package;
  const freeTrialDays = currentPackage?.free_trial_days;
  const packageShow = (pack: MembershipPackage) => {
    const isSelected = currentPackage?.package_name === pack.package_name;
    const showingRecommendFlag =
      checkIsChoosingSubbingMembershipPackageWithNameOnly({
        subbingPackage: currentUser?.membership?.membership_package,
        checkingPackage: pack,
      });
    return (
      <div
        className={classNames(styles.package, {
          [styles.active]: isSelected,
        })}
        onClick={() => {
          setCurrentPackage(pack);
          addMembershipClickEvent({
            position: pack.package_name,
            screenName: screenName,
          });
        }}
      >
        {showingRecommendFlag && (
          <div className={styles.recommendFlag}>{subStatusText}</div>
        )}
        <div className={styles.packageInfo}>
          <div className={styles.label}>
            {pack.package_name !== MembershipPackageName.Platinum ? (
              <Icon
                name={`${pack.package_name}-label`}
                width={34}
                height={40}
              />
            ) : (
              <img
                src="/images/banner-platinum-membership.png"
                height={42}
                style={{ paddingTop: '6px', height: '42px' }}
              />
            )}
            {isSelected && (
              <span className={styles.name}>
                {packageLabel(pack.package_name)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };
  const currentMinAmount = () => {
    if (!currentPackage) return 0;
    if (choosingIzanaviPackage && !!izanaviPackage) {
      return currentPackage?.minimum_amount + izanaviPackage?.minimum_amount;
    }
    return currentPackage?.minimum_amount;
  };
  return (
    <div
      className={styles.descriptionSection}
      style={isProcessing ? { pointerEvents: 'none' } : {}}
    >
      {havingMembershipPackage ? (
        <div className={styles.descriptionTitle}>
          メンバーシップのプラン変更
        </div>
      ) : (
        <div className={styles.descriptionTitle}>メンバーシップを選択</div>
      )}
      <div className={styles.packageChooseSection}>
        {!isOpenFromTradeOpeningLP && greenPackage && packageShow(greenPackage)}
        {!isOpenFromTradeOpeningLP &&
          silverPackage &&
          packageShow(silverPackage)}
        {!isOpenFromTradeOpeningLP && goldPackage && packageShow(goldPackage)}
        {platinumPackage && packageShow(platinumPackage)}
      </div>
      <div className={styles.packageName}>
        <div className={styles.avatar}>
          {currentPackage?.package_name !== MembershipPackageName.Platinum ? (
            <Icon
              name={`${currentPackage?.package_name}-label`}
              width={61}
              height={72}
            />
          ) : (
            <img
              src="/images/banner-platinum-membership.png"
              height={75}
              style={{ paddingTop: '6px', height: '75px' }}
            />
          )}
        </div>
        <div className={styles.title}>
          <div className={styles.trialText}>
            {isReSubscription
              ? 'メンバーシップ登録'
              : currentPaymentBy === 'credit'
                ? freeTrialRangeText(freeTrialDays)
                : ''}
          </div>
          {currentMinAmount() > 0 && (
            <div className={styles.text}>
              {isReSubscription
                ? '登録価格は'
                : currentPaymentBy === 'credit'
                  ? '無料体験後は'
                  : ''}
              1日あたり約
              {currentMinAmount()}円
            </div>
          )}
        </div>
      </div>
      <div className={styles.featureGroup}>
        {(currentPackage?.item || []).map((benefit, index) => {
          return (
            <div className={styles.feature} key={`benefit-${index}`}>
              <div className={styles.icon}>
                <img
                  src={benefit.icon_link}
                  width={30}
                  height={30}
                  style={{ width: '30px', height: '30px' }}
                />
              </div>
              <span className={styles.featureText}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: featureContent(benefit.text, true),
                  }}
                ></div>
                {benefit.sub_text && (
                  <div
                    className={styles.smallText}
                    dangerouslySetInnerHTML={{
                      __html: featureContent(benefit.sub_text, true),
                    }}
                  ></div>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {currentUser &&
        currentUser?.membership?.next_membership_package &&
        currentUser?.membership?.next_package_change_timing && (
          <div className={styles.nextPackageInfo}>
            <span className={styles.text}>
              {dayjs(
                currentUser?.membership?.next_package_change_timing,
              ).format('YYYY/MM/DD')}
              から{'\n'}「
              {packageLabel(
                currentUser?.membership?.next_membership_package?.package_name,
              )}{' '}
              メンバーシップ
              {membershipPricePackagePeriod(
                currentUser?.membership?.next_membership_package?.recurring,
              )}
              プラン」に変更
            </span>
          </div>
        )}
      {currentUser &&
        currentUser?.membership?.coupon &&
        currentUser?.membership?.coupon?.promotion_type ===
          DISCOUNT_TO_100_YEN_SILVER_EVENT && (
          <div className={styles.silverDiscountInfo}>
            <span className={styles.text}>
              {dayjs(currentUser?.membership?.coupon?.coupon_end_at).format(
                'YYYY/MM/DD',
              )}
              に「Silverメンバーシップ」{'\n'}のキャンペーン価格が終了
            </span>
          </div>
        )}
      {/* {!isMobile && (
        <div className={styles.alertText}>
          <span>メンバーシップのヘルプは</span>
          <a href={MEMBERSHIP_HELP_LINK} target="_blank">
            こちら
          </a>
        </div>
      )} */}
      {!currentAuth && (
        <div
          className={styles.loginBtn}
          onClick={() => {
            showLogin({ showingTitle: 'login' });
            addMembershipClickEvent({
              position: 'login_button',
              screenName: screenName,
            });
          }}
        >
          ログインして無料体験しよう!
        </div>
      )}
    </div>
  );
};

export default MembershipDescription;
